import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { L10nTranslationService } from 'angular-l10n';
import { DataObject } from '../../../../../../lib/models/data-object.models';
import { AvailabilityColumns, CatalogMethod, CatalogsService, Column } from '@miq/catalog';
import { DetailsOverlayData, OverlayField } from '../details-overlay/details-overlay.model';
import { DETAILS_MODAL_CONFIG } from './config';
import {
  DetailsMapping,
  DetailsMenu,
  DetailsModalData,
  DetailsModalSetup,
  DetailsSpace,
  LovLookups,
} from './details-modal.model';
import { getMenu } from './details-modal.constants';
import { SuiteType } from '../item.model';
import { DetailsOverlayService } from '../details-overlay/details-overlay.service';
import { StackingPlanService } from '../../../../../stacking-plan.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { LovValues } from '../../../../../../lib/stacking-plan.model';
import { ActionsToolbarService } from '../../../../actions-toolbar/actions-toolbar.service';
import { FiltersService } from '../../../../../../lib/filters-toolbar/filters.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ALL_DISABLED, getTenancyStatusConfigurations } from '@miq/domain';
import { AvailabilityType } from '../../../../../models/space/availability-space.class';
import { SpaceSaveServiceFactoryService } from './space-save-factory.service';
import { UserInfoResolver, INFO_USER_RESOLVER_TOKEN } from '@miq/user';
import { StackPlanViewStateService } from '../../../../../state/sp-view-state.service';
@UntilDestroy()
@Component({
  selector: 'sp-details-modal',
  templateUrl: './details-modal.component.html',
  styleUrls: ['./details-modal.component.scss'],
  providers: [DetailsOverlayService, SpaceSaveServiceFactoryService],
  standalone: false,
})
export class DetailsModalComponent implements OnInit, OnDestroy {
  private updateSubsets$ = new BehaviorSubject(null);
  detailsSetup$: BehaviorSubject<DetailsModalSetup> = new BehaviorSubject<DetailsModalSetup>(null);
  setup: DetailsModalSetup;
  detailsFormGroup: FormGroup<{ [key: string]: UntypedFormGroup & UntypedFormControl }>;
  progressbarClass: string;
  editMode: boolean;
  space: DetailsSpace;
  hasEDPWriteAccess = false;
  // * Save changes made by dynamic validation...
  mutableData: DataObject;
  overlayMenu: DetailsMenu[];
  containerRecordFloors: DataObject[];
  unaccountedSpace: number;
  disableMenu = signal(false);
  disableSave$: Observable<boolean> = of(true);
  private _saveInProgress = new BehaviorSubject(false);
  saveInProgress$ = this._saveInProgress.asObservable();

  constructor(
    @Inject(MAT_DIALOG_DATA) details: DetailsModalData,
    private catalogService: CatalogsService,
    private filtersService: FiltersService,
    public detailsOverlayService: DetailsOverlayService,
    public dialogRef: MatDialogRef<DetailsModalComponent>,
    // NOTE: Don't delete the translation service. It's a dependency of details-modal.constants.ts.
    public translationService: L10nTranslationService,
    public stackingPlanService: StackingPlanService,
    public notificationService: NotificationService,
    public actionsToolbarService: ActionsToolbarService,
    private spaceSaveServiceFactory: SpaceSaveServiceFactoryService,
    private stackPlanViewStateService: StackPlanViewStateService,
    @Inject(INFO_USER_RESOLVER_TOKEN) private userInfoResolver: UserInfoResolver
  ) {
    const { space, containerRecordFloors, progressbarClass, unaccountedSpace } = details;
    this.space = space;
    // * Save data that can be mutated...
    this.mutableData = {
      floorNumber: space.floorNumber,
      suiteNumber: space.suiteNumber,
      availabilityType: space.availabilityType,
    };
    this.progressbarClass = progressbarClass;
    this.containerRecordFloors = containerRecordFloors;
    this.unaccountedSpace = unaccountedSpace;
  }

  ngOnInit() {
    this.overlayMenu = this.getOverlayMenu();
    this.hasEDPWriteAccess = this.stackingPlanService.hasEDPWriteAccess();
    this.updateSubsets$.pipe(switchMap(() => this.getCatalog(this.space.suiteType))).subscribe(setup => {
      this.detailsSetup$.next(setup);
    });
  }

  ngOnDestroy(): void {
    // * required by untilDestroy...
  }

  private getCatalog(suiteType: string): Observable<DetailsModalSetup> {
    return this.catalogService
      .getColumns(suiteType, CatalogMethod.Default, {
        mapAllRelations: true,
      })
      .pipe(
        map(catalog => catalog[this.space.suiteType] || []),
        map(sections => sections.reduce((columns, { columns: col }) => [...columns, ...col], [])),
        switchMap(columns => this.generateLookups().pipe(map(lookups => [columns, lookups]))),
        map(([columns, lookups]: DetailsMapping) => ({ columns, config: this.setupSubsets(columns, lookups) })),
        tap(setup => {
          this.setup = setup;
          this.stackPlanViewStateService.setColumns(setup.columns);
          if (!this.detailsFormGroup) {
            this.initFormGroup(setup.config);
          }
        })
      );
  }

  private getOverlayMenu(): DetailsMenu[] {
    const isMenuDisabled = DETAILS_MODAL_CONFIG[this.space?.suiteType]?.disableMenu ?? (() => false);
    this.disableMenu.set(isMenuDisabled(this.space));
    return this.disableMenu() ? [] : getMenu.bind(this)();
  }

  private setupSubsets(columns: Column[], lookups: LovLookups): DetailsOverlayData {
    const config: DetailsOverlayData = DETAILS_MODAL_CONFIG[this.space.suiteType];
    const subsets = config.subsets
      .filter(({ id, inputType, isLinkedOpportunities, labelFn }) =>
        // * filter subsets by columnId...
        this.space.suiteType === SuiteType.Availability
          ? columns.some(column => column.columnId === id) && labelFn(columns)
          : isLinkedOpportunities || labelFn(inputType === 'progressBar' ? this.space : columns)
      )
      .map(subset => {
        // * set subset validators...
        const validators = subset.validators || [];
        const validatorFn = subset.validationFn ? subset.validationFn(this.space) : null;
        const isRequired = this.isFieldRequired(columns, subset);
        //  * set subset values...
        const values = lookups[subset.dataField];
        const unitValues = lookups[subset.unit?.dataField];
        if (validatorFn != null) {
          validators.push(validatorFn);
        }

        let unitRequiredValidators = subset.unit?.validators;
        if (isRequired) {
          validators.push(Validators.required);
          if (unitRequiredValidators == null) {
            unitRequiredValidators = Validators.required;
          }
        }

        return {
          ...subset,
          isRequired,
          ...(values ? { values } : {}),
          ...(unitValues ? { unit: { ...subset.unit, values: unitValues, validators: unitRequiredValidators } } : {}),
          ...(validators.length ? { validators: validators.filter(validator => validator) } : {}),
        };
      });

    return {
      ...config,
      subsets,
    };
  }

  private initFormGroup(config: DetailsOverlayData): void {
    const { subsets, validators: groupValidators, formValidator } = config;
    const form = new UntypedFormGroup({});

    subsets.forEach(({ formGroup, dataField, validators: ctrlValidators, unit }) => {
      let ctrl: UntypedFormControl | UntypedFormGroup;
      if (formGroup) {
        const { dataField: unitField } = unit || {};
        const bindGroupValidators = (
          groupValidators.find(validators => validators.groupName === formGroup)?.validator || []
        ).map(val => val.bind(this));
        ctrl = (form.get(formGroup) as UntypedFormGroup) || new UntypedFormGroup({});
        ctrl.addControl(dataField, new UntypedFormControl(''));
        if (ctrlValidators?.length > 0) {
          ctrl.get(dataField).setValidators(ctrlValidators);
        }
        if (unit) {
          ctrl.addControl(unitField, new UntypedFormControl(''));
          if (unit.validators?.length > 0) {
            ctrl.get(unitField).setValidators(unit.validators);
          }
        }

        ctrl.setValidators(bindGroupValidators);
      } else {
        ctrl = new UntypedFormControl('', ctrlValidators || null);
      }

      form.addControl(formGroup || dataField, ctrl);
    });

    this.#entityLevelFormSubscriptions(form);
    if (formValidator) {
      form.addValidators(formValidator);
    }
    this.detailsFormGroup = form;

    this.disableSave$ = combineLatest([this.detailsFormGroup.statusChanges, this.saveInProgress$]).pipe(
      map(([status, saveInProgress]) => status !== 'VALID' || saveInProgress)
    );
  }

  #entityLevelFormSubscriptions(form: UntypedFormGroup) {
    switch (this.space.suiteType) {
      case SuiteType.Availability:
        this.#availFormSubscriptions(form);
      case SuiteType.Lease:
        this.#leaseFormSubscriptons(form);
    }
  }

  #availFormSubscriptions(form: UntypedFormGroup): void {
    form
      .get('availabilityType')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        this.#updateFloorSuiteCtrlOnValueChange(form, value);

        const leaseTypeCtrl = form.get('leaseType');
        if (leaseTypeCtrl) {
          this.#toggleEnableLeaseCtrl(leaseTypeCtrl, value);
        }
      });

    this.rateControlSubscription(
      form,
      'formMinMonthRentGroup',
      'minAskingRateMo',
      'formMinAskingRateYrGroup',
      'minAskingRateYr'
    );
    this.rateControlSubscription(
      form,
      'formMinAskingRateYrGroup',
      'minAskingRateYr',
      'formMinMonthRentGroup',
      'minAskingRateMo'
    );
    this.rateControlSubscription(
      form,
      'formMaxMonthRentGroup',
      'maxAskingRateMo',
      'formMaxAskingRateYrGroup',
      'maxAskingRateYr'
    );
    this.rateControlSubscription(
      form,
      'formMaxAskingRateYrGroup',
      'maxAskingRateYr',
      'formMaxMonthRentGroup',
      'maxAskingRateMo'
    );
    this.uomControlSubscription(
      form,
      'formMinMonthRentGroup',
      'minAskingRateMoUom',
      'formMinAskingRateYrGroup',
      'minAskingRateYrUom'
    );
    this.uomControlSubscription(
      form,
      'formMinAskingRateYrGroup',
      'minAskingRateYrUom',
      'formMinMonthRentGroup',
      'minAskingRateMoUom'
    );
    this.uomControlSubscription(
      form,
      'formMaxMonthRentGroup',
      'maxAskingRateMoUom',
      'formMaxAskingRateYrGroup',
      'maxAskingRateYrUom'
    );
    this.uomControlSubscription(
      form,
      'formMaxAskingRateYrGroup',
      'maxAskingRateYrUom',
      'formMaxMonthRentGroup',
      'maxAskingRateMoUom'
    );
  }

  /** Sets control subscriptions for rate fields */
  private rateControlSubscription(
    form: UntypedFormGroup,
    sourceGroupName: string,
    sourceFieldName: string,
    targetGroupName: string,
    targetFieldName: string
  ): void {
    const sourceGroup = form.get(sourceGroupName);
    if (sourceGroup) {
      const sourceControl = sourceGroup.get(sourceFieldName);
      if (sourceControl) {
        sourceControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
          const targetGroup = form.get(targetGroupName);
          if (!targetGroup) return;
          const targetControl = targetGroup.get(targetFieldName);
          if (!targetControl) return;
          if (sourceControl['isUpdating']) return;
          sourceControl['isUpdating'] = true;
          if (!value || value.trim() === '') {
            targetControl.setValue(null, { emitEvent: false });
          } else {
            const cleanValue = value.replace(/,/g, '');
            let convertedValue: number;
            if (sourceFieldName.includes('Mo') && targetFieldName.includes('Yr')) {
              convertedValue = parseFloat(cleanValue) * 12;
            } else {
              convertedValue = parseFloat(cleanValue) / 12;
            }
            const formattedValue = this.formatCurrency(convertedValue);
            targetControl.setValue(formattedValue || null, { emitEvent: false });
            this.syncUomBetweenGroups(form, sourceGroupName, targetGroupName, sourceFieldName, targetFieldName);
          }
          setTimeout(() => {
            sourceControl['isUpdating'] = false;
          }, 0);
        });
      }
    }
  }

  /** control subscriptions for UOM fields */
  private uomControlSubscription(
    form: UntypedFormGroup,
    sourceGroupName: string,
    sourceUomField: string,
    targetGroupName: string,
    targetUomField: string
  ): void {
    const sourceGroup = form.get(sourceGroupName);
    if (sourceGroup) {
      const sourceControl = sourceGroup.get(sourceUomField);
      if (sourceControl) {
        sourceControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
          const targetGroup = form.get(targetGroupName);
          if (!targetGroup) return;
          const targetControl = targetGroup.get(targetUomField);
          if (!targetControl) return;
          if (sourceControl['isUpdating']) return;
          sourceControl['isUpdating'] = true;
          targetControl.setValue(value, { emitEvent: false });
          setTimeout(() => {
            sourceControl['isUpdating'] = false;
          }, 0);
        });
      }
    }
  }

  /**  UOM values between source and target groups */
  private syncUomBetweenGroups(
    form: UntypedFormGroup,
    sourceGroupName: string,
    targetGroupName: string,
    sourceFieldName: string,
    targetFieldName: string
  ): void {
    const sourceUomField = sourceFieldName + 'Uom';
    const targetUomField = targetFieldName + 'Uom';
    const sourceGroup = form.get(sourceGroupName);
    if (!sourceGroup) return;
    const sourceUomValue = sourceGroup.get(sourceUomField)?.value;
    const targetGroup = form.get(targetGroupName);
    if (!targetGroup) return;
    const targetUomControl = targetGroup.get(targetUomField);
    if (!targetUomControl) return;
    targetUomControl.setValue(sourceUomValue, { emitEvent: false });
  }

  /** Formats a number as currency */
  formatCurrency(value: number): string {
    return value.toLocaleString('en-US', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }
  #updateFloorSuiteCtrlOnValueChange(form: UntypedFormGroup, value: any) {
    if (!value) return;

    const columns = this.setup?.columns ?? [];
    const configSubsets = this.setup?.config?.subsets ?? [];
    const isRequiredByBusiness = this.space.areFloorAndSuitRequiredValidation(value);

    const fields = ['floorNumber', 'suiteNumber'];

    fields.forEach(field => {
      const control = form.get(field);
      if (!control) return;
      const subset = configSubsets.find(s => s.dataField === field);
      const isRequiredInCatalog = subset ? this.isFieldRequired(columns, subset) : false;
      if (isRequiredInCatalog && isRequiredByBusiness) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }
  #getSubsetElementIndexByColumnId = (id: number) => this.setup.config.subsets.findIndex(s => s.id === id);

  #makeCtrlRequired = (ctrl: AbstractControl, colId: number) => {
    ctrl?.addValidators(Validators.required);
    ctrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.setup.config.subsets.at(this.#getSubsetElementIndexByColumnId(colId)).isRequired = true;
  };

  #makeCtrlNotRequired = (ctrl: AbstractControl, colId) => {
    ctrl?.removeValidators(Validators.required);
    ctrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    this.setup.config.subsets.at(this.#getSubsetElementIndexByColumnId(colId)).isRequired = false;
  };

  #toggleEnableLeaseCtrl(leaseTypeCtrl: AbstractControl, value: AvailabilityType | '') {
    if (this.space.isLeaseTypeEditable(value)) {
      leaseTypeCtrl?.enable();
      if (value === AvailabilityType.Lease) {
        this.#makeCtrlRequired(leaseTypeCtrl, AvailabilityColumns.SpaceDetails_LeaseType);
        return;
      }
      this.#makeCtrlNotRequired(leaseTypeCtrl, AvailabilityColumns.SpaceDetails_LeaseType);
    } else {
      leaseTypeCtrl?.setValue(null, { onlySelf: true, emitEvent: false });
      leaseTypeCtrl?.disable();
      this.#makeCtrlNotRequired(leaseTypeCtrl, AvailabilityColumns.SpaceDetails_LeaseType);
    }
  }

  #leaseFormSubscriptons(form: UntypedFormGroup): void {
    const statusCtrl = form?.get('spaceLeaseStatus');
    const startDateCtrl = form?.get('leaseStartDate');
    const endDateCtrl = form?.get('leaseEndDate');
    const occupancyDateCtrl = form?.get('spaceOccupancyDate');
    if ([statusCtrl, startDateCtrl, endDateCtrl, occupancyDateCtrl].filter(Boolean).length === 4) {
      combineLatest([startDateCtrl.valueChanges, endDateCtrl.valueChanges, occupancyDateCtrl.valueChanges]).subscribe(
        ([startDate, endDate, occupancyDate]) => {
          const defaultValues = getTenancyStatusConfigurations(occupancyDate, startDate, endDate);
          const findSelectedValue = vals =>
            vals.find(lov => lov.key.toLowerCase() === defaultValues.value?.toLowerCase())?.key ?? null;
          const statusConfig = this.setup?.config?.subsets.find(subset => subset.dataField === 'spaceLeaseStatus');
          const selectedValue = defaultValues?.value === ALL_DISABLED ? null : findSelectedValue(statusConfig.values);
          statusCtrl.setValue(selectedValue);
          statusCtrl.updateValueAndValidity();
          this.#updateDropdownValues(statusConfig, defaultValues);
          this.detailsSetup$.next(this.setup);
        }
      );
    }
  }

  #updateDropdownValues(statusConfig, defaultValues): void {
    const isLovDisabled = (lovKey: string) =>
      defaultValues?.options[0] === ALL_DISABLED ||
      !defaultValues?.options?.some((defaultOption: string) => defaultOption.toLowerCase() === lovKey.toLowerCase());

    statusConfig.values = statusConfig.values.map(lov => ({ ...lov, disabled: isLovDisabled(lov.key) }));
  }

  private generateLookups(): Observable<LovLookups> {
    const lookups = DETAILS_MODAL_CONFIG[this.space.suiteType].subsets.reduce((result, subset) => {
      const { dataField, unit, lookupCategory } = subset;
      const { dataField: unitField, lookupCategory: unitLookup } = unit || {};

      return {
        ...result,
        ...(dataField && lookupCategory ? { [dataField]: this.resolveLookup(lookupCategory) } : {}),
        ...(unitField && unitLookup ? { [unitField]: this.resolveLookup(unitLookup) } : {}),
      };
    }, {});

    return forkJoin<{ [x: string]: Observable<LovValues[]> }>(lookups);
  }

  private resolveLookup(lookup: string): Observable<LovValues[]> {
    return this.stackingPlanService.getLovValues(lookup);
  }

  private isFieldRequired(columns: Column[], subset: OverlayField): boolean {
    const exceptions = [AvailabilityColumns.SpaceDetails_LeaseType];
    return columns.some(
      column => !exceptions.includes(column.columnId) && column.columnId === subset.id && column.required
    );
  }

  cancel(): void {
    this.editMode = false;
    // * Restored mutated data...
    if (this.space.suiteType === SuiteType.Availability) {
      Object.entries(this.mutableData || {}).forEach(([key, value]) => (this.space[key] = value));
    }
    this.updateSubsets$.next(null);
  }

  save() {
    try {
      // get payload generator...
      const saveService = this.spaceSaveServiceFactory.getSpaceSaver(this.space);
      // get payload...
      const args = { space: this.space, floors: this.containerRecordFloors, form: this.detailsFormGroup };
      this._saveInProgress.next(true);
      // save
      saveService.save(args).pipe(take(1)).subscribe({ next: this._onSaveComplete, error: this._onError });
    } catch (error) {
      this._onError(error);
      return;
    }
  }

  private _onError = error => {
    this.notificationService.showSnackBack(error.message ?? 'Error occurred while saving the space.', 'close');
    this._saveInProgress.next(false);
  };

  private _onSaveComplete = () => {
    this._saveInProgress.next(false);
    this.dialogRef.close();
    this.stackingPlanService.setSpaceChanges();
    this.filtersService.resetFilter$.next();
  };

  numericChange(ctrl: UntypedFormControl, subset: OverlayField): void {
    const uomCtrl = this.detailsFormGroup.get(subset.formGroup)?.get(subset.unit?.dataField);
    if (!ctrl.value || !uomCtrl || uomCtrl?.value) {
      return;
    }
    // * Auto-select first value of uom's list.
    uomCtrl.setValue(subset.unit.values[0].key);
    uomCtrl.markAsDirty();
  }
}
